import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link2, Check, Share2, Ban, FileDown, ShieldCheck } from "lucide-react";
import Seo from "./Seo";
import PageHero from "./PageHero";
import { Label, Reveal } from "./ui-bits";
import { api } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function ShareWithGp() {
  const [shares, setShares] = useState([]);
  const [form, setForm] = useState({ expires_days: 7, gp_email: "", practice: "" });
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api("/results/shares").then((d) => setShares(d.shares)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setCreated(null);
    try {
      const doc = await api("/results/share", { method: "POST", body: { expires_days: Number(form.expires_days), gp_email: form.gp_email || null, practice: form.practice || null } });
      setCreated(doc);
      setShares((s) => [doc, ...s]);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { window.prompt("Copy link", url); }
  };

  const revoke = async (id) => {
    await api(`/results/shares/${id}`, { method: "DELETE" });
    setShares((s) => s.map((x) => (x.share_id === id ? { ...x, revoked: true } : x)));
    if (created?.share_id === id) setCreated(null);
  };

  const active = shares.filter((s) => !s.revoked && new Date(s.expires_at) > new Date());

  return (
    <div className="card-line rounded-2xl p-6 sm:p-8" data-testid="share-gp">
      <Label><Share2 size={14} /> Share with your GP</Label>
      <p className="text-sm text-mute leading-relaxed mt-3">Create a secure, read-only link to your results summary. It expires automatically, you can revoke it at any time, and nothing is attached to an email — the summary is generated fresh each time it is opened.</p>

      <form onSubmit={create} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 items-end" data-testid="share-form">
        <label className="block"><span className="mono-label text-dusk block mb-2">Link expires in</span>
          <select className="field" value={form.expires_days} onChange={set("expires_days")} data-testid="share-expiry">
            {[1, 3, 7, 14, 30].map((d) => <option key={d} value={d} className="bg-surface">{d} day{d > 1 ? "s" : ""}</option>)}
          </select>
        </label>
        <label className="block"><span className="mono-label text-dusk block mb-2">GP / practice email (optional)</span><input type="email" className="field" placeholder="reception@practice.nhs.uk" value={form.gp_email} onChange={set("gp_email")} data-testid="share-gp-email" /></label>
        <label className="block"><span className="mono-label text-dusk block mb-2">Practice name (optional)</span><input className="field" placeholder="e.g. Standish Medical Practice" value={form.practice} onChange={set("practice")} data-testid="share-practice" /></label>
        <button type="submit" disabled={busy} className="btn-primary !py-2.5 !px-5 text-sm justify-center" data-testid="share-create">{busy ? "Creating…" : form.gp_email ? "Create & email link" : "Create link"}</button>
        {error && <p className="text-rose text-xs sm:col-span-2 lg:col-span-4" data-testid="share-error">{error}</p>}
      </form>

      {created && (
        <div className="mt-5 rounded-xl border border-teal/40 bg-teal/5 p-4" data-testid="share-created">
          <p className="text-sm text-bone/90">{created.emailed ? <>Link emailed to <span className="text-teal">{created.gp_email}</span>. </> : created.gp_email ? <span className="text-amber-300">Link created, but the email could not be sent right now — copy it instead. </span> : null}Expires {fmt(created.expires_at)}.</p>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <input readOnly className="field flex-1 font-mono text-xs" value={created.share_url} onFocus={(e) => e.target.select()} data-testid="share-url" />
            <button type="button" onClick={() => copy(created.share_url)} className="btn-ghost !py-2.5 !px-4 text-sm gap-2" data-testid="share-copy">{copied ? <Check size={14} className="text-teal" /> : <Link2 size={14} />} {copied ? "Copied" : "Copy link"}</button>
          </div>
        </div>
      )}

      {active.length > 0 && (
        <ul className="mt-6 divide-y divide-line text-xs" data-testid="share-list">
          {active.map((s) => (
            <li key={s.share_id} className="py-2.5 flex flex-wrap items-center gap-3" data-testid={`share-row-${s.share_id}`}>
              <span className="font-mono text-dusk">Created {fmt(s.created_at)}</span>
              <span className="text-bone/85 flex-1">Expires {fmt(s.expires_at)}{s.practice ? ` · ${s.practice}` : ""}{s.gp_email ? ` · ${s.gp_email}` : ""} · opened {s.view_count} time{s.view_count === 1 ? "" : "s"}</span>
              <button type="button" onClick={() => revoke(s.share_id)} className="inline-flex items-center gap-1 text-dusk hover:text-rose transition-colors" data-testid={`share-revoke-${s.share_id}`}><Ban size={13} /> Revoke</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SharePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(undefined);
  const [error, setError] = useState("");

  useEffect(() => {
    api(`/share/${token}`).then(setMeta).catch((e) => { setMeta(null); setError(e.message); });
  }, [token]);

  const pdfUrl = `${API}/api/share/${token}/pdf`;
  return (
    <>
      <Seo title="Shared results summary | Androgen Health" description="A patient has shared a secure, read-only summary of their testosterone results." />
      <PageHero label="Shared results — read only" title={meta ? <>Results summary for <span className="serif-it text-teal">{meta.patient_name}</span>.</> : meta === null ? "This link is not available." : "Loading…"} lede={meta ? `Shared via the Androgen Health patient portal${meta.practice ? ` with ${meta.practice}` : ""}. ${meta.result_count} result${meta.result_count === 1 ? "" : "s"} on record. Link expires ${fmt(meta.expires_at)}.` : error || ""} safety={false} />
      <section className="pb-24 px-5 sm:px-8" data-testid="share-page">
        <div className="max-w-3xl mx-auto lg:mx-0">
          {meta && (
            <Reveal>
              <div className="card-line rounded-2xl p-8 border-l-2 !border-l-teal" data-testid="share-page-card">
                <Label><ShieldCheck size={14} /> Secure, expiring link</Label>
                <p className="text-mute leading-relaxed mt-4">The PDF opens directly from our server — nothing is attached to an email and the patient can revoke access at any time. It includes latest results against typical UK ranges, trend charts, and the patient's dose diary, to support shared care.</p>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-primary mt-7 gap-2" data-testid="share-page-pdf"><FileDown size={16} /> Open results PDF</a>
                <p className="text-xs text-dusk mt-6">Androgen Health services are provided by High Street Health Ltd t/a Ready Health (Companies House 15044189), 22 High Street, Standish, Wigan WN6 0HL. This summary is not a laboratory report and does not replace clinical review.</p>
              </div>
            </Reveal>
          )}
          {meta === null && <p className="text-mute" data-testid="share-page-error">{error || "This link has expired or been revoked by the patient."}</p>}
        </div>
      </section>
    </>
  );
}
